/**
 * Client-side filter + sort over the loaded variant set.
 *
 * The API has no server-side filter/sort yet (only cursor pagination), so the
 * sidebar drives these pure functions over whatever pages have been loaded.
 * Everything here is deliberately side-effect free and unit-testable; the same
 * filter shape round-trips through the URL (see urlState.ts).
 */

import {
  infoNumber,
  probandSampleName,
  sampleVaf,
  TAG_CATEGORIES,
  tagShortLabel,
  variantTag,
  type Variant,
} from './types'

export type SortKey = 'locus' | 'qual' | 'filter' | 'tag' | 'mlProb' | 'gnomad' | 'ref' | 'gt' | 'vaf' | 'dp'
export type SortDir = 'asc' | 'desc'

export type VariantSort = {
  key: SortKey
  dir: SortDir
}

export type VariantFilters = {
  /** FILTER column values to include (e.g. PASS). Empty = include all. */
  filters: string[]
  /** INFO/TAG categories to include. Empty = include all. */
  tags: string[]
  /** Chromosomes to include. Empty = include all. */
  chroms: string[]
  /** Minimum QUAL; null = no threshold. */
  minQual: number | null
  /** Minimum proband DP; null = no threshold. */
  minDp: number | null
  /** Proband VAF inclusive range [0,1]. */
  vafMin: number
  vafMax: number
}

export const DEFAULT_SORT: VariantSort = { key: 'locus', dir: 'asc' }

/**
 * Canonical GRCh38 contigs, chr-prefixed. Shown in the sidebar so reviewers can
 * jump to any chromosome even before its variants have loaded (the facet counts
 * only ever know about loaded rows). Selecting one scopes the fetch server-side.
 */
export const CANONICAL_CHROMS: readonly string[] = [
  ...Array.from({ length: 22 }, (_, i) => `chr${i + 1}`),
  'chrX',
  'chrY',
  'chrM',
]

export function emptyFilters(): VariantFilters {
  return { filters: [], tags: [], chroms: [], minQual: null, minDp: null, vafMin: 0, vafMax: 1 }
}

export function activeFilterCount(f: VariantFilters): number {
  let n = 0
  if (f.filters.length > 0) n += 1
  if (f.tags.length > 0) n += 1
  if (f.chroms.length > 0) n += 1
  if (f.minQual !== null) n += 1
  if (f.minDp !== null) n += 1
  if (f.vafMin > 0 || f.vafMax < 1) n += 1
  return n
}

/** Rank a TAG by confidence order (lower = better); unknown tags sort last. */
function tagRank(tag: string | null): number {
  if (tag === null) return TAG_CATEGORIES.length + 1
  const idx = (TAG_CATEGORIES as readonly string[]).indexOf(tag)
  return idx === -1 ? TAG_CATEGORIES.length : idx
}

function probandVaf(v: Variant): number | null {
  const name = probandSampleName(v)
  if (!name) return null
  return sampleVaf(v.samples[name])
}

function probandDp(v: Variant): number | null {
  const name = probandSampleName(v)
  if (!name) return null
  const dp = v.samples[name]?.DP
  return typeof dp === 'number' ? dp : null
}

/** Alt-allele dosage of the proband GT (0 hom-ref, 1 het, 2 hom-alt); null when absent. */
function probandGtDosage(v: Variant): number | null {
  const name = probandSampleName(v)
  if (!name) return null
  const gt = v.samples[name]?.GT
  if (!gt || gt.length === 0) return null
  return gt.reduce((n: number, a) => n + (typeof a === 'number' && a > 0 ? 1 : 0), 0)
}

export function matchesFilters(v: Variant, f: VariantFilters): boolean {
  if (f.filters.length > 0 && !v.filter.some((x) => f.filters.includes(x))) return false
  if (f.tags.length > 0) {
    const tag = variantTag(v)
    if (tag === null || !f.tags.includes(tag)) return false
  }
  if (f.chroms.length > 0 && !f.chroms.includes(v.chrom)) return false
  if (f.minQual !== null && (v.qual === null || v.qual < f.minQual)) return false
  if (f.minDp !== null) {
    const dp = probandDp(v)
    if (dp === null || dp < f.minDp) return false
  }
  if (f.vafMin > 0 || f.vafMax < 1) {
    const vaf = probandVaf(v)
    if (vaf === null || vaf < f.vafMin || vaf > f.vafMax) return false
  }
  return true
}

export function applyFilters(variants: Variant[], f: VariantFilters): Variant[] {
  return variants.filter((v) => matchesFilters(v, f))
}

/**
 * One active-filter "chip": a stable id, a short human label, and the filter
 * state that results from removing just this constraint. Drives the removable
 * chip bar above the table (GEN-3139). Returns [] when no filter is active.
 */
export type FilterChip = { id: string; label: string; next: VariantFilters }

export function describeActiveFilters(f: VariantFilters): FilterChip[] {
  const chips: FilterChip[] = []
  for (const t of f.tags) {
    chips.push({
      id: `tag:${t}`,
      label: `TAG: ${tagShortLabel(t)}`,
      next: { ...f, tags: f.tags.filter((x) => x !== t) },
    })
  }
  for (const v of f.filters) {
    chips.push({
      id: `filter:${v}`,
      label: `FILTER: ${v || '(empty)'}`,
      next: { ...f, filters: f.filters.filter((x) => x !== v) },
    })
  }
  for (const c of f.chroms) {
    chips.push({ id: `chrom:${c}`, label: c, next: { ...f, chroms: f.chroms.filter((x) => x !== c) } })
  }
  if (f.minQual !== null) {
    chips.push({ id: 'minQual', label: `QUAL >= ${f.minQual}`, next: { ...f, minQual: null } })
  }
  if (f.minDp !== null) {
    chips.push({ id: 'minDp', label: `DP >= ${f.minDp}`, next: { ...f, minDp: null } })
  }
  if (f.vafMin > 0 || f.vafMax < 1) {
    chips.push({ id: 'vaf', label: `VAF ${f.vafMin}-${f.vafMax}`, next: { ...f, vafMin: 0, vafMax: 1 } })
  }
  return chips
}

function compareVariants(a: Variant, b: Variant, key: SortKey): number {
  switch (key) {
    case 'qual':
      return (a.qual ?? -Infinity) - (b.qual ?? -Infinity)
    case 'filter':
      return (a.filter[0] ?? '').localeCompare(b.filter[0] ?? '')
    case 'tag':
      return tagRank(variantTag(a)) - tagRank(variantTag(b))
    case 'mlProb':
      return (infoNumber(a, 'ML_PROB') ?? -Infinity) - (infoNumber(b, 'ML_PROB') ?? -Infinity)
    case 'gnomad':
      return (infoNumber(a, 'AF_genomes') ?? -Infinity) - (infoNumber(b, 'AF_genomes') ?? -Infinity)
    case 'ref':
      return `${a.ref}>${a.alt.join(',')}`.localeCompare(`${b.ref}>${b.alt.join(',')}`)
    case 'gt':
      return (probandGtDosage(a) ?? -1) - (probandGtDosage(b) ?? -1)
    case 'vaf':
      return (probandVaf(a) ?? -Infinity) - (probandVaf(b) ?? -Infinity)
    case 'dp':
      return (probandDp(a) ?? -Infinity) - (probandDp(b) ?? -Infinity)
    case 'locus':
    default:
      if (a.chrom !== b.chrom) return a.chrom.localeCompare(b.chrom, undefined, { numeric: true })
      return a.pos - b.pos
  }
}

export function sortVariants(variants: Variant[], sort: VariantSort): Variant[] {
  const sorted = [...variants].sort((a, b) => compareVariants(a, b, sort.key))
  return sort.dir === 'desc' ? sorted.reverse() : sorted
}

export type FacetCounts = {
  filters: Record<string, number>
  tags: Record<string, number>
  chroms: Record<string, number>
}

/** Tally FILTER, TAG, and chromosome occurrences across the loaded set for the sidebar. */
export function computeFacetCounts(variants: Variant[]): FacetCounts {
  const filters: Record<string, number> = {}
  const tags: Record<string, number> = {}
  const chroms: Record<string, number> = {}
  for (const v of variants) {
    for (const fv of v.filter) filters[fv] = (filters[fv] ?? 0) + 1
    const tag = variantTag(v)
    if (tag !== null) tags[tag] = (tags[tag] ?? 0) + 1
    chroms[v.chrom] = (chroms[v.chrom] ?? 0) + 1
  }
  return { filters, tags, chroms }
}
